/* Native theme media behavior: bounded loops, accessible controls and isolated galleries. */
(() => {
  'use strict';
  if (window.__meddeyMediaLayouts) return;
  window.__meddeyMediaLayouts = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function cloneForDisplay(source) {
    const clone = source.cloneNode(true);
    clone.dataset.mhcClone = '';
    clone.setAttribute('aria-hidden', 'true');
    [clone, ...clone.querySelectorAll('*')].forEach(el => {
      el.removeAttribute('id');
      el.removeAttribute('data-mhc-original');
      for (const name of el.getAttributeNames()) if (name.startsWith('data-shopify')) el.removeAttribute(name);
      if (el.matches('a,button,input,select,textarea,[tabindex]')) el.tabIndex = -1;
    });
    return clone;
  }
  class ContinuousRow extends HTMLElement {
    connectedCallback() { this.initFrame = requestAnimationFrame(() => this.initialize()); }
    initialize() {
      if (!this.isConnected) return;
      this.original = this.querySelector('[data-mhc-original]');
      this.viewport = this.querySelector('[data-mhc-viewport]');
      this.belt = this.querySelector('[data-mhc-belt]');
      if (!this.original || !this.viewport || !this.belt) return;
      this.abort?.abort(); this.abort = new AbortController();
      this.userPaused = this.classList.contains('mhc-user-paused');
      this.resize?.disconnect(); this.visibility?.disconnect();
      /* ponytail: prepare() moves this.original into the cycle, which changes its measured width and
         re-fires this observer -> rebuild every frame, and every cloned <img> re-requests.
         The flag drops the self-triggered pass. Genuine viewport/content resizes still get through. */
      this.resize = new ResizeObserver(() => { if (!this.writing) this.schedule(); });
      this.resize.observe(this.viewport); this.resize.observe(this.original);
      this.visibility = new IntersectionObserver(entries => { this.classList.toggle('mhc-offscreen', !entries[0].isIntersecting); });
      this.visibility.observe(this);
      this.viewport.tabIndex = 0;
      this.viewport.setAttribute('role', 'region');
      if (!this.viewport.hasAttribute('aria-label')) this.viewport.setAttribute('aria-label', 'Scrolling content');
      this.viewport.setAttribute('aria-keyshortcuts', 'Space');
      const setPaused = value => { this.userPaused=value; this.classList.toggle('mhc-user-paused',value); this.viewport.dataset.paused=String(value); };
      this.addEventListener('click', event => {
        if (!this.viewport.contains(event.target)) return;
        if (event.target.closest('a[href],button,input,select,textarea')) { setPaused(true); return; }
        setPaused(!this.userPaused);
      }, { signal: this.abort.signal });
      this.viewport.addEventListener('keydown', event => {
        if ((event.key === ' ' || event.key === 'Enter') && event.target === this.viewport) { event.preventDefault(); setPaused(!this.userPaused); }
      }, { signal: this.abort.signal });
      this.addEventListener('shopify:block:select', () => setPaused(true), { signal: this.abort.signal });
      this.addEventListener('focusin', event => {
        if (event.target.closest('[data-mhc-original]')) this.classList.add('mhc-keyboard');
      }, { signal: this.abort.signal });
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (!this.contains(document.activeElement)) { this.classList.remove('mhc-keyboard'); this.viewport.scrollLeft = 0; }
      }), { signal: this.abort.signal });
      reduced.addEventListener('change', () => this.schedule(), { signal: this.abort.signal });
      this.schedule();
    }
    schedule() { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.prepare()); }
    prepare() {
      if (!this.isConnected || !this.original.children.length) return;
      const width = this.viewport.clientWidth;
      const originalWidth = this.original.getBoundingClientRect().width;
      if (!width || !originalWidth) return;
      const gap = Number.parseFloat(getComputedStyle(this.original).columnGap) || 0;
      const count = Math.min(24, Math.max(1, Math.ceil((width + gap) / (originalWidth + gap))));
      const signature = [Math.round(width), Math.round(originalWidth), Math.round(gap), count, reduced.matches].join(':');
      if (signature === this.signature) return;
      /* ponytail: hard cap on rebuilds per viewport width. Each rebuild re-clones every card, and a
         cloned <img> re-requests its src. If the measured width oscillates (sub-pixel flex jitter,
         late-loading images) we keep the last good layout rather than cloning forever.
         Ceiling: a genuine resize resets the counter, so real responsive changes still rebuild. */
      const widthKey = Math.round(width);
      if (this.lastWidthKey !== widthKey) { this.lastWidthKey = widthKey; this.rebuilds = 0; }
      if ((this.rebuilds = (this.rebuilds || 0) + 1) > 5) return;
      this.signature = signature;
      this.writing = true;
      const cycle = document.createElement('div'); cycle.className = 'mhc-marquee-cycle';
      cycle.append(this.original);
      for (let i = 1; i < count; i++) cycle.append(cloneForDisplay(this.original));
      this.belt.replaceChildren(cycle, cloneForDisplay(cycle));
      this.belt.style.setProperty('--mhc-loop-gap', gap + 'px');
      this.belt.style.setProperty('--mhc-loop-distance', (originalWidth * count + gap * count) + 'px');
      this.dataset.mhcReady = '';
      requestAnimationFrame(() => requestAnimationFrame(() => { this.writing = false; }));
    }
    disconnectedCallback() { cancelAnimationFrame(this.initFrame); cancelAnimationFrame(this.frame); this.resize?.disconnect(); this.visibility?.disconnect(); this.abort?.abort(); }
  }
  if (!customElements.get('meddey-continuous-row')) customElements.define('meddey-continuous-row', ContinuousRow);
  // The platform's development-preview iframe also needs an accessible name.
  const labelPreview = () => {
    const frame = document.getElementById('PBarNextFrame');
    if (frame?.tagName === 'IFRAME' && frame.src.startsWith('https://cdn.shopify.com/shopifycloud/preview-bar/')) {
      if (!frame.title) frame.title = 'Shopify theme preview toolbar';
      return true;
    }
    return false;
  };
  if (!labelPreview() && window.Shopify?.theme?.role !== 'main') {
    const previewObserver = new MutationObserver(() => { if (labelPreview()) previewObserver.disconnect(); });
    previewObserver.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => previewObserver.disconnect(), 20000);
  }
  document.addEventListener('click', event => {
    const thumb = event.target.closest('[data-mhc-spotlight-src]');
    if (thumb) {
      const gallery = thumb.closest('.meddey-spotlight-gallery');
      const image = gallery?.querySelector('.meddey-spotlight-main-img');
      if (!image) return;
      image.src = thumb.dataset.mhcSpotlightSrc;
      if (thumb.dataset.mhcSpotlightSrcset) image.srcset = thumb.dataset.mhcSpotlightSrcset;
      else image.removeAttribute('srcset');
      image.alt = thumb.dataset.mhcImageAlt || image.alt;
      gallery.querySelectorAll('[data-mhc-spotlight-src]').forEach(button => {
        button.setAttribute('aria-pressed', String(button === thumb));
        button.classList.toggle('meddey-spotlight-thumb-btn--active', button === thumb);
      });
    }
    const pause = event.target.closest('[data-hero-pause]');
    if (pause) {
      const slider = pause.closest('[data-hero-slider]');
      if (slider) {
        const paused = slider.toggleAttribute('data-hero-user-paused');
        pause.setAttribute('aria-pressed', String(paused));
        pause.textContent = paused ? pause.dataset.resumeLabel : pause.dataset.pauseLabel;
      }
    }
  });
  // ==========================================================================
  // GLOBAL MODERN SLIDER ARCHITECTURE (<meddey-slider>)
  // Touch swipe gestures, fluid drag-to-scroll, accessible arrows & indicators
  // ==========================================================================
  class MeddeySlider extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      requestAnimationFrame(() => this.initSlider());
    }

    disconnectedCallback() {
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
      if (this._mo) {
        this._mo.disconnect();
        this._mo = null;
      }
    }

    initSlider() {
      this.track = this.querySelector('[data-slider-track], .meddey-row-slider-track, .meddey-slider-track, .meddey-offers-grid, .product-list__inner') || this.firstElementChild;
      if (!this.track) return;

      // Ensure horizontal track never traps vertical scroll
      this.removeAttribute('data-lenis-prevent');
      this.track.removeAttribute('data-lenis-prevent');

      this.prevBtn = this.querySelector('[data-slider-prev], .meddey-carousel-nav-btn--prev');
      this.nextBtn = this.querySelector('[data-slider-next], .meddey-carousel-nav-btn--next');

      // 1. Proportional step calculation
      const getScrollStep = () => {
        const item = this.track.firstElementChild;
        if (item) {
          const style = window.getComputedStyle(this.track);
          const gap = parseFloat(style.columnGap || style.gap) || 16;
          const cardWidth = item.getBoundingClientRect().width;
          const visibleCards = Math.max(1, Math.floor((this.track.clientWidth + gap) / (cardWidth + gap)));
          return (cardWidth + gap) * visibleCards;
        }
        return this.track.clientWidth * 0.85;
      };

      // 2. Navigation Button Handling
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.track.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.track.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
        });
      }

      // 3. Arrow state synchronizer (auto disable on edges & auto-hide when content fits)
      const updateArrows = () => {
        const sl = Math.ceil(this.track.scrollLeft);
        const max = this.track.scrollWidth - this.track.clientWidth;
        const canScroll = max > 3;

        this.classList.toggle('meddey-slider--no-scroll', !canScroll);
        if (!canScroll) {
          this.setAttribute('data-no-scroll', '');
        } else {
          this.removeAttribute('data-no-scroll');
        }

        if (this.prevBtn) {
          if (!canScroll) {
            this.prevBtn.style.setProperty('display', 'none', 'important');
          } else {
            this.prevBtn.style.removeProperty('display');
            const atStart = sl <= 2;
            this.prevBtn.disabled = atStart;
            this.prevBtn.classList.toggle('is-disabled', atStart);
            this.prevBtn.setAttribute('aria-disabled', String(atStart));
          }
        }
        if (this.nextBtn) {
          if (!canScroll) {
            this.nextBtn.style.setProperty('display', 'none', 'important');
          } else {
            this.nextBtn.style.removeProperty('display');
            const atEnd = sl >= max - 4;
            this.nextBtn.disabled = atEnd;
            this.nextBtn.classList.toggle('is-disabled', atEnd);
            this.nextBtn.setAttribute('aria-disabled', String(atEnd));
          }
        }
      };

      this.updateArrows = updateArrows;

      this.track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows, { passive: true });

      if (window.ResizeObserver) {
        this._ro = new ResizeObserver(() => updateArrows());
        this._ro.observe(this.track);
        this._ro.observe(this);
      }

      if (window.MutationObserver) {
        this._mo = new MutationObserver(() => updateArrows());
        this._mo.observe(this.track, { childList: true, subtree: true });
      }

      updateArrows();
      setTimeout(updateArrows, 100);
      setTimeout(updateArrows, 500);

      // 4. Mouse Drag-to-Scroll (Desktop fallback for tracks without .meddey-swipe)
      if (!this.track.classList.contains('meddey-swipe')) {
        let isDown = false;
        let startX = 0;
        let scrollStart = 0;
        let hasDragged = false;

        this.track.addEventListener('mousedown', (e) => {
          if (e.button !== 0) return;
          isDown = true;
          hasDragged = false;
          startX = e.pageX - this.track.offsetLeft;
          scrollStart = this.track.scrollLeft;
        });

        const stopDrag = () => {
          if (!isDown) return;
          isDown = false;
          this.track.classList.remove('is-dragging');
          if (hasDragged) {
            const cancelClick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
            };
            window.addEventListener('click', cancelClick, { capture: true, once: true });
            setTimeout(() => {
              window.removeEventListener('click', cancelClick, { capture: true });
              hasDragged = false;
            }, 80);
          }
        };

        window.addEventListener('mouseup', stopDrag);
        window.addEventListener('mouseleave', stopDrag);

        this.track.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          const x = e.pageX - this.track.offsetLeft;
          const walk = (x - startX) * 1.35;
          if (Math.abs(walk) > 10) {
            if (!hasDragged) {
              hasDragged = true;
              this.track.classList.add('is-dragging');
            }
            e.preventDefault();
            this.track.scrollLeft = scrollStart - walk;
          }
        });
      }

      // 5. Keyboard Navigation
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.track.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.track.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
        }
      });
    }
  }

  // Global anti-jitter scroll stabilizer (disables card pointer events during scroll to eliminate hover flutter)
  if (typeof window !== 'undefined' && !window.__meddeyScrollStabilizerInit) {
    window.__meddeyScrollStabilizerInit = true;
    let scrollDebounce;
    window.addEventListener('scroll', () => {
      if (!document.documentElement.classList.contains('is-scrolling')) {
        document.documentElement.classList.add('is-scrolling');
      }
      clearTimeout(scrollDebounce);
      scrollDebounce = setTimeout(() => {
        document.documentElement.classList.remove('is-scrolling');
      }, 90);
    }, { passive: true });
  }

  if (!customElements.get('meddey-slider')) {
    customElements.define('meddey-slider', MeddeySlider);
  }

  // Global smooth deals scroller for any legacy onclick callers
  window.meddeyScrollDeals = function(sectionId, offset) {
    const el = document.getElementById('meddey-offers-scroll-' + sectionId) || document.querySelector('#shopify-section-' + sectionId + ' .meddey-offers-grid');
    if (!el) return;
    const card = el.firstElementChild;
    const scrollAmount = card ? (card.getBoundingClientRect().width + 16) * (offset > 0 ? 2 : -2) : offset;
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  /* ==========================================================================
     MEDDEY UNIVERSAL TABLE AUTO-PATCHER
     Detects unstyled tables, strips inline presentation attributes (border, width, bgcolor),
     normalizes headers, and wraps in responsive card container.
     ========================================================================== */
  function patchMeddeyTables(scope) {
    const root = scope || document;
    const tables = root.querySelectorAll('table:not([class*="cart"]):not([class*="datepicker"]):not([class*="calendar"])');
    tables.forEach((table) => {
      if (table.dataset.meddeyPatched === 'true') return;
      table.dataset.meddeyPatched = 'true';
      table.classList.add('meddey-styled-table');

      // Strip destructive inline attributes
      table.removeAttribute('border');
      table.removeAttribute('cellpadding');
      table.removeAttribute('cellspacing');
      table.removeAttribute('width');
      table.removeAttribute('bgcolor');
      table.removeAttribute('align');
      if (table.style.width && !table.style.width.includes('%')) {
        table.style.width = '100%';
      }

      // Check cells inside table for inline width / borders
      table.querySelectorAll('td, th').forEach((cell) => {
        cell.removeAttribute('border');
        cell.removeAttribute('bgcolor');
        cell.removeAttribute('cellspacing');
        cell.removeAttribute('cellpadding');
        if (cell.style.width && cell.style.width.includes('px')) {
          cell.style.width = '';
        }
      });

      // Normalize thead if missing but first row acts as header
      if (!table.querySelector('thead')) {
        const firstRow = table.querySelector('tr');
        if (firstRow) {
          const cells = Array.from(firstRow.children);
          const hasTh = cells.some((c) => c.tagName === 'TH');
          const isHeaderLike = hasTh || cells.some((c) => /feature|spec|item|attribute|parameter|detail|description|value|qty/i.test(c.textContent || ''));
          if (isHeaderLike) {
            const thead = document.createElement('thead');
            thead.appendChild(firstRow);
            table.insertBefore(thead, table.firstElementChild);
            cells.forEach((c) => {
              if (c.tagName === 'TD') {
                const th = document.createElement('th');
                th.innerHTML = c.innerHTML;
                Array.from(c.attributes).forEach((attr) => th.setAttribute(attr.name, attr.value));
                c.replaceWith(th);
              }
            });
          }
        }
      }

      // Automatically wrap in .meddey-table-responsive if not already wrapped
      const parent = table.parentElement;
      if (!parent.classList.contains('meddey-table-responsive') && !parent.classList.contains('table-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'meddey-table-responsive';
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('tabindex', '0');
        wrapper.setAttribute('aria-label', 'Specification Table');
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      } else if (parent.classList.contains('table-wrapper') && !parent.classList.contains('meddey-table-responsive')) {
        parent.classList.add('meddey-table-responsive');
      }
    });
  }

  /* Declared above the call sites below: this script is deferred, so the
     else-branch runs initMeddeyTrustChips() synchronously. Leaving this const
     further down put it in the temporal dead zone and threw
     "Cannot access TRUST_CHIP_ICONS before initialization". */
  // Icon per chip title, so a JS-retitled chip never keeps the wrong glyph.
  const TRUST_CHIP_ICONS = {
    Replacement: '<path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M21 21v-5h-5"></path>',
    Refund: '<circle cx="12" cy="12" r="9"></circle><path d="M8.5 7.5h7"></path><path d="M8.5 10.5h7"></path><path d="M12.5 7.5a3 3 0 0 1 0 6H8.5l5.5 4.5"></path>',
    Exchange: '<polyline points="17 2 21 6 17 10"></polyline><path d="M21 6H8a4 4 0 0 0-4 4"></path><polyline points="7 22 3 18 7 14"></polyline><path d="M3 18h13a4 4 0 0 0 4-4"></path>',
    Warranty: '<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="m9 12 2 2 4-4"></path>',
    _default: '<path d="M3 7v6h6"></path><path d="M3.5 13a9 9 0 1 0 2.2-9.2L3 7"></path>'
  };

  // Run on DOMContentLoaded and immediately if document is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      patchMeddeyTables();
      initMeddeyTrustChips();
    });
  } else {
    patchMeddeyTables();
    initMeddeyTrustChips();
  }

  // Support dynamic sections and interactive elements
  document.addEventListener('shopify:section:load', (e) => {
    patchMeddeyTables(e.target);
    initMeddeyTrustChips(e.target);
  });
  document.addEventListener('shopify:section:select', (e) => {
    patchMeddeyTables(e.target);
    initMeddeyTrustChips(e.target);
  });
  window.addEventListener('load', () => {
    patchMeddeyTables();
    initMeddeyTrustChips();
  });
  document.addEventListener('click', (e) => {
    if (e.target.closest('[role="tab"], .product-tabs__tab-item, summary, .mhc-summary')) {
      setTimeout(() => {
        patchMeddeyTables();
        initMeddeyTrustChips();
      }, 50);
    }
  });

  /* ==========================================================================
     MEDDEY DYNAMIC TRUST CHIPS DETECTOR
     Inspects PDP description & DOM content to dynamically reveal and populate
     Warranty and Return / Replacement / Refund / Exchange chips if not already rendered server-side.
     If not detected in metadata, tags, or description, the chips remain strictly hidden.
     ========================================================================== */
  function parsePolicyFromText(text) {
    if (!text) return null;

    // 1. Forward match: "X[+] days [easy] replacement/refund/return/exchange [policy]"
    // e.g. "14+ days refund", "14 days refund", "3 days replacement", "5-day refund", "7+ days exchange"
    const forwardMatch = text.match(/(?:\b|^)(\d+\s*\+?)\s*(?:-| )*days?\s*(?:easy\s*)?(replacement|replace|refund|money\s*back|return|returns|exchange)(?:\s+policy)?\b/i);

    // 2. Reverse match: "replacement/refund/return within/in/up to X[+] days"
    // e.g. "replacement within 14+ days", "refund in 14 days", "return up to 30 days"
    const reverseMatch = text.match(/\b(replacement|replace|refund|money\s*back|return|returns|exchange)\s*(?:within|in|of|upto|up\s+to)?\s*(\d+\s*\+?)\s*(?:-| )*days?\b/i);

    let days = '';
    let type = '';

    if (forwardMatch) {
      days = forwardMatch[1].replace(/\s+/g, '').trim();
      type = forwardMatch[2].toLowerCase();
    } else if (reverseMatch) {
      type = reverseMatch[1].toLowerCase();
      days = reverseMatch[2].replace(/\s+/g, '').trim();
    }

    // 3. Loose sentence match if both day count and keyword exist in nearby context
    if (!days || !type) {
      const looseMatch = text.match(/(?:\b|^)(\d+\s*\+?)\s*(?:-| )*days?\b[^\.\n]*?\b(replacement|replace|refund|money\s*back|return|returns|exchange)\b/i);
      const looseRevMatch = text.match(/\b(replacement|replace|refund|money\s*back|return|returns|exchange)\b[^\.\n]*?\b(\d+\s*\+?)\s*(?:-| )*days?\b/i);
      if (looseMatch) {
        days = looseMatch[1].replace(/\s+/g, '').trim();
        type = looseMatch[2].toLowerCase();
      } else if (looseRevMatch) {
        type = looseRevMatch[1].toLowerCase();
        days = looseRevMatch[2].replace(/\s+/g, '').trim();
      }
    }

    if (days && type) {
      if (type.indexOf('replace') !== -1) {
        return { title: 'Replacement', subtitle: `${days} Days Replacement` };
      }
      if (type.indexOf('refund') !== -1 || type.indexOf('money') !== -1) {
        return { title: 'Refund', subtitle: `${days} Days Refund` };
      }
      if (type.indexOf('exchange') !== -1) {
        return { title: 'Exchange', subtitle: `${days} Days Exchange` };
      }
      if (type.indexOf('return') !== -1) {
        return { title: 'Easy Returns', subtitle: `${days} Days Return Policy` };
      }
    }

    // 4. Pattern: "X[+] days policy"
    const policyDaysMatch = text.match(/(?:\b|^)(\d+\s*\+?)\s*(?:-| )*days?\s*policy\b/i);
    if (policyDaysMatch) {
      const d = policyDaysMatch[1].replace(/\s+/g, '').trim();
      return { title: 'Easy Returns', subtitle: `${d} Days Policy` };
    }

    // 5. Non-numeric phrases
    if (/\b(replacement\s+only|only\s+replacement)\b/i.test(text)) {
      return { title: 'Replacement', subtitle: 'Replacement Only' };
    }
    if (/\b(easy\s+replacement)\b/i.test(text)) {
      return { title: 'Replacement', subtitle: 'Easy Replacement' };
    }
    if (/\b(non[- ]returnable|not\s+returnable|no\s+returns?)\b/i.test(text)) {
      return { title: 'Policy', subtitle: 'Non-Returnable' };
    }
    if (/\b(easy\s+(?:return|returns))\b/i.test(text)) {
      return { title: 'Easy Returns', subtitle: 'Easy Returns' };
    }

    return null;
  }

  function parseWarrantyFromText(text) {
    if (!text) return null;

    // 1. Forward match: "X[+] year/yr/month/mo [brand/mfr] warranty"
    const forwardMatch = text.match(/(?:\b|^)(\d+\s*\+?)\s*(?:-| )*(years?|yrs?|months?|mos?)\s*(?:brand\s*|manufacturer\s*)?warranty\b/i);

    // 2. Reverse match: "[brand/mfr] warranty of/for X[+] year/month"
    const reverseMatch = text.match(/\b(?:brand\s*|manufacturer\s*)?warranty\s*(?:of|for|upto|up\s+to)?\s*(\d+\s*\+?)\s*(?:-| )*(years?|yrs?|months?|mos?)\b/i);

    let num = '';
    let unit = '';

    if (forwardMatch) {
      num = forwardMatch[1].replace(/\s+/g, '').trim();
      unit = forwardMatch[2].toLowerCase();
    } else if (reverseMatch) {
      num = reverseMatch[1].replace(/\s+/g, '').trim();
      unit = reverseMatch[2].toLowerCase();
    }

    if (num && unit) {
      const isYear = unit.charAt(0) === 'y';
      const isSingle = num === '1';
      const unitStr = isYear ? (isSingle ? 'Year' : 'Years') : (isSingle ? 'Month' : 'Months');
      return { title: 'Warranty', subtitle: `${num} ${unitStr} Brand Warranty` };
    }

    if (/\b(lifetime\s+warranty)\b/i.test(text)) {
      return { title: 'Warranty', subtitle: 'Lifetime Warranty' };
    }
    if (/\b(brand\s+warranty|manufacturer\s+warranty)\b/i.test(text)) {
      return { title: 'Warranty', subtitle: 'Brand Warranty' };
    }

    return null;
  }


  function trustChipSvg(title) {
    return '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      (TRUST_CHIP_ICONS[title] || TRUST_CHIP_ICONS._default) + '</svg>';
  }

  function setTrustChipIcon(chip, title) {
    const iconEl = chip.querySelector('.meddey-trust-chip__icon');
    if (iconEl) iconEl.innerHTML = trustChipSvg(title);
  }

  function initMeddeyTrustChips(scope) {
    const root = scope || document;
    const chipsContainers = root.querySelectorAll('[data-meddey-trust-chips]');
    if (!chipsContainers.length) return;

    chipsContainers.forEach((container) => {
      const productId = container.dataset.productId;
      let fullDescText = '';

      // Collect text sources:
      // A. Product-specific description by ID if present
      if (productId) {
        const descById = document.getElementById('meddey-description-' + productId);
        if (descById) {
          fullDescText += ' ' + (descById.innerText || descById.textContent || '');
        }
      }

      // B. Inside the closest product section
      const productScope = container.closest('.shopify-section--main-product, .product') || root;
      const localDescEls = productScope.querySelectorAll('.product-meta__description, .product__description, [data-product-description], .product-form__description');
      localDescEls.forEach((el) => {
        fullDescText += ' ' + (el.innerText || el.textContent || '');
      });

      // C. Product content tabs or source containers document-wide (excluding header, footer, cart, recommendations)
      const globalDescEls = document.querySelectorAll('[data-mhc-description-source], .product-tabs__tab-item-content, .product-tabs__content, .product-tabs__tab-content, #product-description, .product-description, .product-tabs');
      globalDescEls.forEach((el) => {
        if (!el.closest('footer, header, nav, .cart, .mini-cart, .shopify-section--product-recommendations, .shopify-section--recently-viewed-products')) {
          fullDescText += ' ' + (el.innerText || el.textContent || '');
        }
      });

      fullDescText = fullDescText.trim();

      // Free Shipping chip controls
      const shippingChip = container.querySelector('[data-chip="shipping"], .meddey-trust-chip--shipping');
      if (container.dataset.freeShippingDisabled === 'true') {
        if (shippingChip) shippingChip.remove();
      } else if (shippingChip && container.dataset.freeShippingSubtitle) {
        const subEl = shippingChip.querySelector('[data-chip-subtitle], .meddey-trust-chip__subtitle');
        if (subEl) subEl.textContent = container.dataset.freeShippingSubtitle;
      }

      const warrantyChip = container.querySelector('[data-chip="warranty"]');
      const returnChip = container.querySelector('[data-chip="return"]');

      // 1. Dynamic Warranty Detection
      const warrantyData = parseWarrantyFromText(fullDescText);
      if (warrantyData) {
        if (warrantyChip) {
          const titleEl = warrantyChip.querySelector('[data-chip-title]');
          const subEl = warrantyChip.querySelector('[data-chip-subtitle]');
          if (titleEl) titleEl.textContent = warrantyData.title;
          if (subEl) subEl.textContent = warrantyData.subtitle;
          setTrustChipIcon(warrantyChip, warrantyData.title);
          warrantyChip.style.display = '';
          warrantyChip.classList.remove('is-hidden');
          warrantyChip.removeAttribute('hidden');
        } else {
          const newChip = document.createElement('div');
          newChip.className = 'meddey-trust-chip meddey-trust-chip--warranty';
          newChip.dataset.chip = 'warranty';
          newChip.innerHTML = `
            <div class="meddey-trust-chip__icon" aria-hidden="true">${trustChipSvg(warrantyData.title)}</div>
            <div class="meddey-trust-chip__content">
              <span class="meddey-trust-chip__title" data-chip-title>${warrantyData.title}</span>
              <span class="meddey-trust-chip__subtitle" data-chip-subtitle>${warrantyData.subtitle}</span>
            </div>
          `;
          container.appendChild(newChip);
        }
      } else if (warrantyChip && (warrantyChip.style.display === 'none' || warrantyChip.classList.contains('is-hidden') || !warrantyChip.querySelector('[data-chip-subtitle]')?.textContent.trim())) {
        warrantyChip.remove();
      }

      // 2. Dynamic Return / Replacement / Refund / Exchange Detection
      const policyData = parsePolicyFromText(fullDescText);
      if (policyData) {
        if (returnChip) {
          const titleEl = returnChip.querySelector('[data-chip-title]');
          const subEl = returnChip.querySelector('[data-chip-subtitle]');
          if (titleEl) titleEl.textContent = policyData.title;
          if (subEl) subEl.textContent = policyData.subtitle;
          setTrustChipIcon(returnChip, policyData.title);
          returnChip.style.display = '';
          returnChip.classList.remove('is-hidden');
          returnChip.removeAttribute('hidden');
        } else {
          const newChip = document.createElement('div');
          newChip.className = 'meddey-trust-chip meddey-trust-chip--returns';
          newChip.dataset.chip = 'return';
          newChip.innerHTML = `
            <div class="meddey-trust-chip__icon" aria-hidden="true">${trustChipSvg(policyData.title)}</div>
            <div class="meddey-trust-chip__content">
              <span class="meddey-trust-chip__title" data-chip-title>${policyData.title}</span>
              <span class="meddey-trust-chip__subtitle" data-chip-subtitle>${policyData.subtitle}</span>
            </div>
          `;
          container.appendChild(newChip);
        }
      } else if (returnChip && (returnChip.style.display === 'none' || returnChip.classList.contains('is-hidden') || !returnChip.querySelector('[data-chip-subtitle]')?.textContent.trim())) {
        returnChip.remove();
      }
    });
  }

  window.patchMeddeyTables = patchMeddeyTables;
  window.initMeddeyTrustChips = initMeddeyTrustChips;
})();

/* Theme "Buy it now": once Zecpe has tagged it (zecpe-btn) its capture listener takes the click and this never runs.
   This only handles a tap that lands before that, and hands the same button to Zecpe's buy-now flow. */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-meddey-buy-now]');
  if (!button || button.disabled || button.dataset.busy) return;
  event.preventDefault();
  const form = button.closest('form');
  if (!form) return;
  if (!form.reportValidity()) return;
  button.dataset.busy = 'true';

  // Gateway off in Theme settings -> Checkout: add the item and use Shopify's own
  // checkout immediately, skipping the 4s wait for a gateway that is not loading.
  if (window.meddeyZecpeEnabled !== true) {
    const root = window.themeVariables?.routes?.rootUrl || '/';
    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch((window.themeVariables?.routes?.cartAddUrl || root + 'cart/add') + '.js', {
        method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'This item could not be added to your cart.');
      }
      window.location.href = root + 'checkout';
    } catch (error) {
      form.dispatchEvent(new CustomEvent('cart-notification:show', {
        bubbles: true, cancelable: true, detail: { status: 'error', error: error.message }
      }));
    } finally {
      delete button.dataset.busy;
      button.removeAttribute('aria-busy');
    }
    return;
  }

  setTimeout(() => delete button.dataset.busy, 6000);
  try { if (typeof window.triggerZecpeSetup === 'function') window.triggerZecpeSetup(); } catch (error) {}

  let tries = 0;
  const attempt = () => {
    if (typeof window.HandleBuyNowZecpe === 'function' && typeof window.ZecpeCheckVariant === 'function') {
      window.ZecpeCheckVariant(button, form);
      return;
    }
    if (++tries <= 16) { setTimeout(attempt, 250); return; }
    // Zecpe never loaded (~4s): add this item, then the store's usual checkout hand-off
    const done = () => (typeof window.meddeyGoToCheckout === 'function' ? window.meddeyGoToCheckout(button) : (window.location.href = '/checkout'));
    fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } }).then(done, done);
  };
  attempt();
});

/* Gallery share button: native share sheet where the device has one, copy-link otherwise */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-meddey-share]');
  if (!button) return;
  event.preventDefault();
  button.classList.remove('is-shared');
  void button.offsetWidth;
  button.classList.add('is-shared');

  const url = button.dataset.shareUrl || location.href;
  const title = button.dataset.shareTitle || document.title;
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      return;
    } catch (error) {
      if (error.name === 'AbortError') return;
    }
  }

  let copied = false;
  try {
    await navigator.clipboard.writeText(url);
    copied = true;
  } catch (error) {}
  button.dataset.tip = copied ? 'Link copied' : 'Could not copy link';
  clearTimeout(button.tipTimer);
  button.tipTimer = setTimeout(() => delete button.dataset.tip, 1800);
});

/* ---------------------------------------------------------------------------
   MEDDEY VIDEO ENGINE

   One player behaviour everywhere: in-grid cards, the product gallery slide and
   the hover preview on product cards.

   Rules it enforces, in order:
     - nothing loads or plays until it is actually in front of the shopper
     - exactly one video plays at a time, page wide
     - a saved-data or 2g connection never autoplays; the shopper taps to start
     - muted by default, one tap for sound, two taps to open the source
     - reduced-motion preferences are respected
   YouTube is driven with postMessage rather than loading its IFrame API, so
   this adds no third party script to the page.
   --------------------------------------------------------------------------- */
(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const CLICK_GRACE = 260;
  const HOVER_DWELL = 350;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  const isFrugal = () =>
    reducedMotion.matches ||
    !!(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) ||
    !!window.MeddeyConfig?.reduceBackgroundWork?.();

  let playing = null;

  const ytCommand = (frame, func) => {
    if (!frame || !frame.src) return;
    frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
  };

  const frameOf = (stage) => stage.querySelector('iframe[data-yt-src]');
  const videoOf = (stage) => stage.querySelector('video.meddey-in-grid-video__player');

  function setAspect(stage, ratio) {
    if (ratio > 0 && Number.isFinite(ratio)) {
      stage.style.setProperty('--video-aspect', String(ratio));
      stage.classList.toggle('is-portrait', ratio < 0.9);
    }
  }

  function stop(stage) {
    if (!stage) return;
    videoOf(stage)?.pause();
    ytCommand(frameOf(stage), 'pauseVideo');
    stage.classList.remove('is-playing');
    if (playing === stage) playing = null;
  }

  function play(stage, options) {
    const userInitiated = !!(options && options.userInitiated);
    if (!stage || (isFrugal() && !userInitiated)) return;
    if (playing && playing !== stage) stop(playing);
    playing = stage;

    const frame = frameOf(stage);
    if (frame) {
      if (!frame.src) frame.src = frame.dataset.ytSrc;
      else ytCommand(frame, 'playVideo');
      stage.classList.add('is-playing');
      return;
    }

    const video = videoOf(stage);
    if (video) {
      video.muted = stage.dataset.muted !== '0';
      video.play().then(() => stage.classList.add('is-playing')).catch(() => {});
    }
  }

  function toggleSound(stage) {
    const frame = frameOf(stage);
    const video = videoOf(stage);
    const wasMuted = stage.dataset.muted !== '0';

    if (!frame && !video) return;
    play(stage, { userInitiated: true });

    if (frame) ytCommand(frame, wasMuted ? 'unMute' : 'mute');
    else video.muted = !wasMuted;

    stage.dataset.muted = wasMuted ? '0' : '1';
    stage.classList.toggle('is-unmuted', wasMuted);
    stage.querySelector('[data-video-hit]')?.setAttribute('aria-pressed', String(wasMuted));
  }

  /* Only the stage in view plays, and it stops the moment it leaves. */
  const inView = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        play(entry.target);
        fillChannel(entry.target);
      } else {
        stop(entry.target);
      }
    });
  }, { threshold: 0.55 });

  /* Channel name for the caption, one oEmbed lookup per video id, only for a
     card that is actually on screen. Failure is silent: the title stands alone. */
  const channelCache = new Map();

  function fillChannel(stage) {
    const id = stage.dataset.ytId;
    const slot = stage.closest('.meddey-in-grid-video')?.querySelector('[data-video-channel]');
    if (!id || !slot || slot.dataset.filled === '1') return;
    slot.dataset.filled = '1';

    const show = (name) => {
      if (!name) return;
      slot.textContent = name + ': ';
      slot.hidden = false;
    };

    if (channelCache.has(id)) {
      show(channelCache.get(id));
      return;
    }

    fetch('https://www.youtube.com/oembed?format=json&url=https://www.youtube.com/watch?v=' + id)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const name = data && data.author_name;
        channelCache.set(id, name || '');
        show(name);
      })
      .catch(() => channelCache.set(id, ''));
  }

  function observeStages(scope) {
    (scope || document).querySelectorAll('[data-meddey-video]').forEach((stage) => {
      if (stage.dataset.observed === '1') return;
      stage.dataset.observed = '1';

      const video = videoOf(stage);
      if (video) {
        const applyAspect = () => setAspect(stage, video.videoWidth / video.videoHeight);
        if (video.readyState >= 1) applyAspect();
        else video.addEventListener('loadedmetadata', applyAspect, { once: true });
      }

      const hit = stage.querySelector('[data-video-hit]');
      if (hit) hit.setAttribute('aria-pressed', 'false');
      inView.observe(stage);
    });
  }

  document.addEventListener('click', (event) => {
    const hit = event.target.closest && event.target.closest('[data-video-hit]');
    if (!hit) return;
    event.preventDefault();

    const stage = hit.closest('[data-meddey-video]');
    if (!stage || stage._clickTimer) return;

    // Hold the single click so a double click can cancel it.
    stage._clickTimer = setTimeout(() => {
      stage._clickTimer = null;
      toggleSound(stage);
    }, CLICK_GRACE);
  });

  document.addEventListener('dblclick', (event) => {
    const hit = event.target.closest && event.target.closest('[data-video-hit]');
    if (!hit) return;
    event.preventDefault();

    const stage = hit.closest('[data-meddey-video]');
    if (stage && stage._clickTimer) {
      clearTimeout(stage._clickTimer);
      stage._clickTimer = null;
    }

    const url = hit.dataset.watchUrl;
    if (url) window.open(url, '_blank', 'noopener');
  });

  /* Product card hover preview. The markup carries the source; the player is
     built on first hover and only after a dwell, so brushing past a grid of
     cards costs nothing. Cards without both an image and a video never emit
     the wrapper, so there is nothing to hover. */
  function buildPreview(wrap) {
    if (isFrugal() || wrap.dataset.built === '1') return;
    wrap.dataset.built = '1';

    const type = wrap.dataset.videoType;
    const src = wrap.dataset.videoSrc;
    if (!src) return;

    if (type === 'mp4') {
      const video = wrap.querySelector('video.meddey-card-video-player');
      if (video && !video.src) video.src = video.dataset.src || src;
    } else if (type === 'youtube') {
      const frame = document.createElement('iframe');
      frame.className = 'meddey-card-video-frame';
      frame.title = '';
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      frame.allow = 'autoplay; encrypted-media; picture-in-picture';
      frame.src = 'https://www.youtube-nocookie.com/embed/' + src +
        '?autoplay=1&mute=1&loop=1&playlist=' + src +
        '&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1';
      wrap.appendChild(frame);
    }
  }

  document.addEventListener('pointerenter', (event) => {
    const card = event.target.closest && event.target.closest('.product-item');
    const wrap = card && card.querySelector('.meddey-card-video-wrap');
    if (!wrap) return;

    clearTimeout(wrap._dwell);
    wrap._dwell = setTimeout(() => {
      buildPreview(wrap);
      wrap.classList.add('is-previewing');
      const video = wrap.querySelector('video.meddey-card-video-player');
      if (video) video.play().catch(() => {});
    }, HOVER_DWELL);
  }, { capture: true, passive: true });

  document.addEventListener('pointerleave', (event) => {
    const card = event.target.closest && event.target.closest('.product-item');
    const wrap = card && card.querySelector('.meddey-card-video-wrap');
    if (!wrap) return;

    clearTimeout(wrap._dwell);
    wrap.classList.remove('is-previewing');

    const video = wrap.querySelector('video.meddey-card-video-player');
    if (video && !video.paused) {
      video.pause();
      video.currentTime = 0;
    }
    const frame = wrap.querySelector('iframe.meddey-card-video-frame');
    if (frame) frame.remove();
    wrap.dataset.built = '';
  }, { capture: true, passive: true });

  /* Network got worse, or the tab went away: stop whatever is running. */
  if (connection && connection.addEventListener) {
    connection.addEventListener('change', () => {
      if (isFrugal()) stop(playing);
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop(playing);
  });

  document.addEventListener('DOMContentLoaded', () => observeStages());
  document.addEventListener('shopify:section:load', (event) => observeStages(event.target));
  observeStages();

  window.meddeyObserveVideoStages = observeStages;
})();

/* Sticky buy bar quantity mirror.
   The bar cannot carry its own [name="quantity"] — it submits the main product
   form, and two fields of that name would send two values. So these buttons
   drive the real selector on the page and just display its value. */
(() => {
  const mainInput = () =>
    document.querySelector('.product-form__quantity .quantity-selector__input') ||
    document.querySelector('.product-form__quantity input[name="quantity"]');

  const render = () => {
    const input = mainInput();
    if (!input) return;
    document.querySelectorAll('[data-sticky-qty-value]').forEach((el) => {
      el.textContent = input.value || '1';
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-sticky-qty-step]');
    if (!button) return;
    event.preventDefault();

    const input = mainInput();
    if (!input) return;

    const step = Number(input.step) > 0 ? Number(input.step) : 1;
    const min = input.min !== '' ? Number(input.min) : 1;
    const max = input.max !== '' ? Number(input.max) : Infinity;
    const direction = Number(button.dataset.stickyQtyStep);

    let next = (Number(input.value) || min) + direction * step;
    next = Math.min(max, Math.max(min, next));

    input.value = String(next);
    // input-number and the cart listen on these, so fire both.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    render();
  });

  // Keep the mirror honest when the shopper uses the real selector.
  document.addEventListener('input', (event) => {
    if (event.target.closest && event.target.closest('.product-form__quantity')) render();
  });
  document.addEventListener('change', (event) => {
    if (event.target.closest && event.target.closest('.product-form__quantity')) render();
  });

  document.addEventListener('DOMContentLoaded', render);
  render();
})();

/* YouTube & Embedded Video Gap Scrubber & Click-Blocker Neutralizer */
(() => {
  const patchVideos = () => {
    document.querySelectorAll('.rte .video-container, .rte [class*="video-container"], .rte .video-wrapper').forEach((el) => {
      el.style.setProperty('padding-top', '0', 'important');
      el.style.setProperty('padding-bottom', '0', 'important');
      el.style.setProperty('height', 'auto', 'important');
    });

    document.querySelectorAll('.rte .click-blocker, .rte .video-container .click-blocker').forEach((b) => {
      b.remove();
    });

    document.querySelectorAll('.rte p').forEach((p) => {
      if (p.children.length === 1 && (p.firstElementChild.tagName === 'BR' || p.innerHTML.trim() === '')) {
        p.remove();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchVideos);
  } else {
    patchVideos();
  }
  document.addEventListener('shopify:section:load', patchVideos);
  document.addEventListener('variant:changed', patchVideos);
})();
